/**
 * Event bus. See ARCHITECTURE.md §5.
 *
 * Events are PAST-TENSE FACTS, never commands. Events emitted during a fixed step are
 * queued and dispatched once, at the end of that step (frame order 2f), so every listener
 * observes a consistent world. Presentation-only code may use emitImmediate.
 */

const MAX_QUEUE = 4096;

export class EventBus {
  constructor() {
    this._handlers = new Map();   // type -> Set<fn>
    this._queueType = new Array(MAX_QUEUE);
    this._queuePayload = new Array(MAX_QUEUE);
    this._queueLen = 0;
    this._flushing = false;
    this._dropped = 0;
    /** Debug: set true to console.log every event. */
    this.trace = false;
    /** Harness: rolling log of the last N events for assertions in the playtest driver. */
    this.log = null;              // set to an array to start recording
  }

  on(type, fn) {
    let set = this._handlers.get(type);
    if (!set) { set = new Set(); this._handlers.set(type, set); }
    set.add(fn);
    return () => this.off(type, fn);
  }

  once(type, fn) {
    const wrap = (p) => { this.off(type, wrap); fn(p); };
    return this.on(type, wrap);
  }

  off(type, fn) {
    const set = this._handlers.get(type);
    if (set) set.delete(fn);
  }

  /** Queue an event for dispatch at the end of the current fixed step. */
  emit(type, payload) {
    if (this._queueLen >= MAX_QUEUE) { this._dropped++; return; }
    this._queueType[this._queueLen] = type;
    this._queuePayload[this._queueLen] = payload;
    this._queueLen++;
  }

  /**
   * Dispatch now, synchronously. PRESENTATION ONLY — no simulation listener may act on an
   * immediate event, because ordering is not guaranteed relative to the fixed step.
   */
  emitImmediate(type, payload) {
    this._dispatch(type, payload);
  }

  _dispatch(type, payload) {
    if (this.trace) console.log('[bus]', type, payload);
    if (this.log) { this.log.push({ type, payload }); if (this.log.length > 2048) this.log.shift(); }
    const set = this._handlers.get(type);
    if (!set || set.size === 0) return;
    for (const fn of set) {
      try {
        fn(payload);
      } catch (err) {
        console.error(`[bus] handler for "${type}" threw:`, err);
      }
    }
  }

  /**
   * Dispatch everything queued. Called by core exactly once per fixed step.
   * Events emitted *by* handlers are appended and processed in the same flush, so a
   * cascade resolves within the step — but with a depth guard so a feedback loop cannot
   * hang the frame.
   */
  flush() {
    if (this._flushing) return;
    this._flushing = true;
    let guard = 0;
    while (this._queueLen > 0) {
      if (++guard > 16) {
        console.warn('[bus] cascade depth exceeded; dropping', this._queueLen, 'events');
        this._queueLen = 0;
        break;
      }
      const n = this._queueLen;
      // Snapshot this wave, then clear, so handlers can queue for the next wave.
      const types = this._queueType.slice(0, n);
      const payloads = this._queuePayload.slice(0, n);
      this._queueLen = 0;
      for (let i = 0; i < n; i++) this._dispatch(types[i], payloads[i]);
    }
    this._flushing = false;
    if (this._dropped) {
      console.warn(`[bus] dropped ${this._dropped} events (queue overflow)`);
      this._dropped = 0;
    }
  }

  clear() {
    this._queueLen = 0;
    this._handlers.clear();
  }

  /** Number of live handlers, for leak detection in the harness. */
  handlerCount() {
    let n = 0;
    for (const set of this._handlers.values()) n += set.size;
    return n;
  }
}

/**
 * Canonical event names. Import these instead of typing string literals — a typo in a
 * string literal fails silently, a typo in an identifier fails loudly.
 * Keep in sync with ARCHITECTURE.md §5.1.
 */
export const EV = {
  GAME_STARTED: 'game:started',
  GAME_PHASE_CHANGED: 'game:phaseChanged',
  GAME_TIME_EXTENDED: 'game:timeExtended',
  GAME_TIME_WARNING: 'game:timeWarning',
  GAME_OVER: 'game:over',

  POTHOLE_TARGETED: 'pothole:targeted',
  POTHOLE_ENTERED: 'pothole:entered',
  POTHOLE_EXITED: 'pothole:exited',
  POTHOLE_THUMPED: 'pothole:thumped',

  REPAIR_STARTED: 'repair:started',
  REPAIR_PROGRESS: 'repair:progress',
  REPAIR_MILESTONE: 'repair:milestone',
  REPAIR_COMPLETED: 'repair:completed',
  REPAIR_FAILED: 'repair:failed',

  PATCH_CURED: 'patch:cured',
  PATCH_RUINED: 'patch:ruined',

  COMBO_INCREASED: 'combo:increased',
  COMBO_BROKEN: 'combo:broken',
  COMBO_TIER_REACHED: 'combo:tierReached',

  CONE_THREADED: 'cone:threaded',
  CONE_SLALOM_CHAINED: 'cone:slalomChained',
  CONE_SLALOM_BROKEN: 'cone:slalomBroken',
  CONE_HIT: 'cone:hit',
  CONE_DEPLOYED: 'cone:deployed',

  VEHICLE_COLLIDED: 'vehicle:collided',
  VEHICLE_LANDED: 'vehicle:landed',
  VEHICLE_AIRBORNE: 'vehicle:airborne',
  VEHICLE_DRIFT_STARTED: 'vehicle:driftStarted',
  VEHICLE_DRIFT_ENDED: 'vehicle:driftEnded',
  VEHICLE_SURFACE_CHANGED: 'vehicle:surfaceChanged',
  VEHICLE_GEAR_CHANGED: 'vehicle:gearChanged',

  TRAFFIC_NEAR_MISS: 'traffic:nearMiss',
  TRAFFIC_SPOOKED: 'traffic:spooked',
  TRAFFIC_HIT: 'traffic:hit',

  WORLD_WEATHER_CHANGED: 'world:weatherChanged',
  WORLD_BLOCK_STREAMED: 'world:blockStreamed',

  UI_ANNOUNCE: 'ui:announce',
};
